/**
 * test-panel.tsx — Khung phải của màn Dạy nhân viên AI (kiểu HelpKnow), 2 thẻ cố định:
 *
 *   • Hướng dẫn AI     — chat với trợ lý dạy AI (AI Master): nói yêu cầu bằng lời → trợ lý đề xuất sửa
 *                        tính cách / quy tắc / kịch bản / kiến thức → bấm "Áp dụng" (API đề xuất sẵn có).
 *   • Kiểm tra hỏi đáp — đóng vai khách chat thử (sandbox thật, chạy đúng pipeline): hiện bước đang chạy,
 *                        câu trả lời tách tối đa 3 bong bóng, nút "Chi tiết" dưới mỗi câu trả lời (nguồn +
 *                        quá trình suy luận + trợ lý tối ưu), "Xoá phiên", "Khách giả lập" (nạp hồ sơ một
 *                        khách thật trong CRM — chỉ đọc).
 *
 * Tách từ DemoChat cũ trong ai-train-page.tsx — giữ cùng props ({ botId, botName, botEmoji }).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import {
  Check, ChevronDown, GraduationCap, Info, Loader2, MessageSquareText, RotateCcw, SendHorizonal, UserRound, X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { apiError } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'
import { scrollContainerToEnd } from '@/lib/scroll-to-end'
import { cn } from '@/lib/utils'
import { useAiDataSources } from '@/hooks/use-ai'
import { useContacts } from '@/hooks/use-contacts'
import {
  createTestConversation, simulateTurn, openGuideSession, sendGuideMessage, fetchPendingProposal,
  applyProposal, rejectProposal, TRAIN_BOT_RELOAD_EVENT, type GuideProposal,
} from '@/hooks/use-ai-train-test'
import { PROPOSAL_SUBTYPE_LABELS, PROPOSAL_TARGET_LABELS } from '@/hooks/use-ai-improve'
import { traceSourceSummary } from '../data-sources-lib'
import { TurnDetailDialog, type TestTurn } from './turn-detail-dialog'
import { AutoStopNoticeView } from './auto-stop-dialog'
import { busyStepLabel, SLOW_HINT_MS } from './train-lib'

export interface TrainTestPanelProps { botId: string; botName: string; botEmoji: string }

type PanelTab = 'guide' | 'test'

export function TrainTestPanel({ botId, botName, botEmoji }: TrainTestPanelProps) {
  const { t } = useTranslation('aiTrainUi')
  const [tab, setTab] = useState<PanelTab>('test')
  const [guideSeed, setGuideSeed] = useState('')
  // Phiên thử dùng chung cho cả 2 thẻ (Hướng dẫn AI phân tích được phiên chat thử vừa rồi).
  const convRef = useRef<string | null>(null)

  return (
    <Tabs value={tab} onValueChange={(v) => setTab(v as PanelTab)} className="flex min-h-0 flex-1 flex-col">
      <TabsList className="w-full">
        <TabsTrigger value="guide" className="flex-1 gap-1.5"><GraduationCap className="h-3.5 w-3.5" /> {t('testPanel.tabGuide')}</TabsTrigger>
        <TabsTrigger value="test" className="flex-1 gap-1.5"><MessageSquareText className="h-3.5 w-3.5" /> {t('testPanel.tabTest')}</TabsTrigger>
      </TabsList>
      {/* forceMount: đổi thẻ không mất cuộc trò chuyện đang dở */}
      <TabsContent value="guide" forceMount className="mt-2 min-h-0 flex-1 flex-col data-[state=active]:flex data-[state=inactive]:hidden">
        <GuideChat botId={botId} convRef={convRef} seed={guideSeed} onSeedUsed={() => setGuideSeed('')} />
      </TabsContent>
      <TabsContent value="test" forceMount className="mt-2 min-h-0 flex-1 flex-col data-[state=active]:flex data-[state=inactive]:hidden">
        <TestChat
          botId={botId} botName={botName} botEmoji={botEmoji} convRef={convRef}
          onGuide={(text) => { setGuideSeed(text); setTab('guide') }}
        />
      </TabsContent>
    </Tabs>
  )
}

// ══════════════════════ Kiểm tra hỏi đáp ══════════════════════

type Row =
  | { kind: 'turn'; turn: TestTurn; looked?: string | null }
  | { kind: 'error'; key: string; text: string }

let turnSeq = 0

/** "(trên kênh thật sẽ chờ 2–3 giây, các tin cách nhau 1–3 giây)" — chat thử không chờ thật. */
function delayNoteOf(h: { replyDelay: { min: number; max: number } | null; bubbleDelay: { min: number; max: number } } | null | undefined): string | null {
  if (!h) return null
  const r = (x: { min: number; max: number }) => (x.min === x.max ? `${x.min}` : `${x.min}–${x.max}`)
  const parts = [h.replyDelay ? i18n.t('aiTrainUi:testPanel.delayWait', { range: r(h.replyDelay) }) : null, i18n.t('aiTrainUi:testPanel.delayBubbles', { range: r(h.bubbleDelay) })].filter(Boolean)
  return i18n.t('aiTrainUi:testPanel.delayNote', { parts: parts.join(', ') })
}

function TestChat({ botId, botName, botEmoji, convRef, onGuide }: TrainTestPanelProps & {
  convRef: React.MutableRefObject<string | null>
  onGuide: (text: string) => void
}) {
  const { t } = useTranslation('aiTrainUi')
  const [rows, setRows] = useState<Row[]>([])
  const [pending, setPending] = useState<string | null>(null) // tin khách đang chờ AI
  const [input, setInput] = useState('')
  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [detailKey, setDetailKey] = useState<string | null>(null)
  const [contact, setContact] = useState<{ id: string; name: string } | null>(null)
  const endRef = useRef<HTMLDivElement>(null)
  const { data: dsData } = useAiDataSources()
  const labelOf = (k: string) => dsData?.sources.find((s) => s.key === k)?.label ?? k

  const busy = pending !== null
  useEffect(() => { scrollContainerToEnd(endRef.current) }, [rows, pending])
  useEffect(() => {
    if (!busy) return
    const timer = setInterval(() => setNow(Date.now()), 500)
    return () => clearInterval(timer)
  }, [busy])

  const turns = useMemo(() => rows.flatMap((r) => (r.kind === 'turn' ? [r.turn] : [])), [rows])
  const detailTurn = turns.find((tn) => tn.key === detailKey) ?? null
  const elapsed = startedAt ? Math.max(0, now - startedAt) : 0

  function resetSession() {
    if (busy) return
    convRef.current = null
    setRows([])
    toast.success(t('testPanel.sessionCleared'))
  }

  async function send() {
    const text = input.trim()
    if (!text || busy) return
    setInput('')
    setPending(text)
    const t0 = Date.now()
    setStartedAt(t0)
    setNow(t0)
    try {
      if (!convRef.current) {
        const conv = await createTestConversation(`Kiểm tra — ${botName}`) // i18n-ignore: tiêu đề hội thoại sandbox lưu vào hệ thống
        
        convRef.current = conv.conversationId
      }
      const res = await simulateTurn({ conversationId: convRef.current, customerText: text, botId, contactId: contact?.id })
      const turn: TestTurn = {
        key: `t${++turnSeq}`,
        customerText: text,
        reply: res.reply,
        bubbles: res.bubbles ?? (res.reply ? [res.reply] : []),
        runId: res.runId,
        handoffReason: res.handoff?.should ? (res.handoff.reason || t('testPanel.defaultHandoffReason')) : null,
        handoffNotice: res.handoff?.should ? (res.handoffNotice ?? null) : null,
        waitNotice: res.waitNotice ?? null,
        delayNote: delayNoteOf(res.humanLike),
        detail: res.detail,
        autoStop: res.autoStop ?? null,
      }
      setRows((r) => [...r, { kind: 'turn', turn, looked: traceSourceSummary(res.trace, labelOf) }])
    } catch (err) {
      setRows((r) => [...r, { kind: 'error', key: `e${++turnSeq}`, text: t('testPanel.turnError', { text, message: apiError(err) }) }])
    } finally {
      setPending(null)
      setStartedAt(null)
    }
  }

  return (
    <>
      <div className="mb-2 flex items-center gap-2">
        <ContactPicker value={contact} onChange={setContact} disabled={busy} />
        <Button variant="ghost" size="sm" className="ml-auto h-8 gap-1" onClick={resetSession} disabled={busy || rows.length === 0} title={t('testPanel.clearTitle')}>
          <RotateCcw className="h-3.5 w-3.5" /> {t('testPanel.clear')}
        </Button>
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto rounded-lg border bg-muted/30 p-3">
        {rows.length === 0 && !busy && (
          <p className="py-10 text-center text-xs text-muted-foreground">
            <Trans t={t} i18nKey="testPanel.emptyHint" components={{ br: <br />, b: <b /> }} />
          </p>
        )}
        {rows.map((r) => r.kind === 'error' ? (
          <p key={r.key} className="text-center text-xs italic text-destructive">{r.text}</p>
        ) : (
          <div key={r.turn.key} className="space-y-1.5">
            <div className="flex justify-end">
              <div className="max-w-[80%] whitespace-pre-wrap rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground">{r.turn.customerText}</div>
            </div>
            {r.turn.waitNotice && (
              <div className="flex justify-start">
                <div className="max-w-[85%] whitespace-pre-wrap rounded-lg border border-dashed bg-background px-3 py-2 text-sm text-muted-foreground" title={t('testPanel.waitNoticeTitle')}>
                  <span className="mr-1">{botEmoji}</span>{r.turn.waitNotice}
                </div>
              </div>
            )}
            {r.turn.bubbles.length > 0 ? r.turn.bubbles.map((b, i) => (
              <div key={i} className="flex justify-start">
                <div className="max-w-[85%] whitespace-pre-wrap rounded-lg border bg-background px-3 py-2 text-sm">
                  {i === 0 && <span className="mr-1">{botEmoji}</span>}{b}
                </div>
              </div>
            )) : r.turn.autoStop ? (
              <AutoStopNoticeView notice={r.turn.autoStop} botEmoji={botEmoji} />
            ) : (
              <>
                {r.turn.handoffNotice && (
                  <div className="flex justify-start">
                    <div className="max-w-[85%] whitespace-pre-wrap rounded-lg border bg-background px-3 py-2 text-sm" title={t('testPanel.handoffNoticeTitle')}>
                      <span className="mr-1">{botEmoji}</span>{r.turn.handoffNotice}
                    </div>
                  </div>
                )}
                <p className="text-center text-xs italic text-muted-foreground">
                  {r.turn.handoffReason
                    ? `${t('testPanel.handoffTo', { reason: r.turn.handoffReason })}${r.turn.handoffNotice ? '' : t('testPanel.noNoticeSent')}`
                    : t('testPanel.noReply')}
                </p>
              </>
            )}
            <div className="flex flex-wrap items-center gap-2 pl-1">
              {r.turn.runId && (
                <button type="button" className="text-[11px] font-medium text-primary hover:underline" onClick={() => setDetailKey(r.turn.key)}>
                  {t('common:actions.details')}
                </button>
              )}
              {r.turn.detail && r.turn.detail.sources.length > 0 && (
                <span className="text-[11px] text-muted-foreground">
                  {t('testPanel.sourceCount', { count: r.turn.detail.sources.length })}
                  {r.turn.detail.sources[0]?.scorePct != null ? t('testPanel.topScore', { pct: r.turn.detail.sources[0].scorePct }) : ''}
                </span>
              )}
              {r.looked && <span className="text-[11px] text-muted-foreground">{t('testPanel.looked', { what: r.looked })}</span>}
              {r.turn.delayNote && <span className="text-[11px] text-muted-foreground">· {r.turn.delayNote}</span>}
            </div>
          </div>
        ))}
        {pending && (
          <div className="space-y-1.5">
            <div className="flex justify-end">
              <div className="max-w-[80%] whitespace-pre-wrap rounded-lg bg-primary/80 px-3 py-2 text-sm text-primary-foreground">{pending}</div>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {botEmoji} {busyStepLabel(elapsed)}
            </p>
            {elapsed >= SLOW_HINT_MS && (
              <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <Info className="h-3 w-3" /> {t('testPanel.slowHint')}
              </p>
            )}
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="mt-2 flex gap-2">
        <Input
          id="demo-chat-input"
          placeholder={t('testPanel.inputPlaceholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send() } }}
          disabled={busy}
        />
        <Button onClick={() => void send()} disabled={!input.trim() || busy}><SendHorizonal className="h-4 w-4" /></Button>
      </div>
      {detailTurn && (
        <TurnDetailDialog
          turn={detailTurn} botId={botId} conversation={turns}
          onClose={() => setDetailKey(null)}
          onGuide={(text) => { setDetailKey(null); onGuide(text) }}
        />
      )}
    </>
  )
}

/** "Khách giả lập": chọn một khách thật trong CRM để AI dùng hồ sơ + trí nhớ của khách đó (chỉ đọc). */
function ContactPicker({ value, onChange, disabled }: {
  value: { id: string; name: string } | null
  onChange: (v: { id: string; name: string } | null) => void
  disabled?: boolean
}) {
  const { t } = useTranslation('aiTrainUi')
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const { data, isFetching } = useContacts({ search: search.trim() || undefined, limit: 12, page: 1 })
  // Bỏ khách ảo do chính chat thử tạo ra (source 'ai_sandbox') — chỉ gợi ý khách THẬT trong CRM.
  const list = (data?.contacts ?? []).filter((c) => c.source !== 'ai_sandbox').slice(0, 8)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 max-w-[60%] gap-1.5" disabled={disabled} title={t('testPanel.contactTitle')}>
          <UserRound className="h-3.5 w-3.5" />
          <span className="truncate">{value ? t('testPanel.simulatedNamed', { name: value.name }) : t('testPanel.simulated')}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <p className="mb-1.5 text-[11px] text-muted-foreground">
          {t('testPanel.contactNote')}
        </p>
        <Input autoFocus className="h-8" placeholder={t('testPanel.contactSearch')} value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="mt-1.5 max-h-56 space-y-0.5 overflow-y-auto">
          <button type="button" className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
            onClick={() => { onChange(null); setOpen(false) }}>
            {!value ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5 opacity-50" />} {t('testPanel.anonymous')}
          </button>
          {isFetching && list.length === 0 && <p className="px-2 py-1 text-xs text-muted-foreground">{t('testPanel.searching')}</p>}
          {list.map((c) => {
            const name = c.crmName || c.fullName || c.phone || t('testPanel.customerFallback')
            return (
              <button key={c.id} type="button"
                className={cn('flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted', value?.id === c.id && 'bg-primary/5')}
                onClick={() => { onChange({ id: c.id, name }); setOpen(false) }}>
                {value?.id === c.id ? <Check className="h-3.5 w-3.5" /> : <UserRound className="h-3.5 w-3.5 opacity-50" />}
                <span className="min-w-0 flex-1 truncate">{name}</span>
                {c.phone && <span className="text-[11px] text-muted-foreground">{c.phone}</span>}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ══════════════════════ Hướng dẫn AI ══════════════════════

type GuideLine = { role: 'user' | 'assistant'; text: string; proposal?: GuideProposal | null; proposalState?: 'applied' | 'rejected' }

const GUIDE_QUICK = [
  'Trả lời ngắn gọn hơn, tối đa 3 câu mỗi tin', // i18n-ignore
  'Xưng "em", gọi khách là "anh/chị", thân thiện', // i18n-ignore
  'Khi khách hỏi giá mà chưa có dữ liệu thì xin số điện thoại để tư vấn', // i18n-ignore
  'Phân tích phiên chat thử vừa rồi và đề xuất cải thiện', // i18n-ignore
]

function GuideChat({ botId, convRef, seed, onSeedUsed }: {
  botId: string
  convRef: React.MutableRefObject<string | null>
  seed: string
  onSeedUsed: () => void
}) {
  const { t } = useTranslation('aiTrainUi')
  const [lines, setLines] = useState<GuideLine[]>([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const sessionRef = useRef<string | null>(null)
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!seed) return
    setInput(seed)
    onSeedUsed()
  }, [seed, onSeedUsed])
  useEffect(() => { scrollContainerToEnd(endRef.current) }, [lines, busy])

  async function send(textArg?: string) {
    const text = (textArg ?? input).trim()
    if (!text || busy) return
    setInput('')
    setLines((l) => [...l, { role: 'user', text }])
    setBusy(true)
    try {
      if (!sessionRef.current) sessionRef.current = (await openGuideSession(convRef.current, botId)).id
      const r = await sendGuideMessage(sessionRef.current, text)
      const proposal = r.proposalId ? await fetchPendingProposal(r.proposalId).catch(() => null) : null
      setLines((l) => [...l, { role: 'assistant', text: r.reply, proposal }])
    } catch (err) {
      setLines((l) => [...l, { role: 'assistant', text: t('testPanel.guideError', { message: apiError(err) }) }])
    } finally {
      setBusy(false)
    }
  }

  async function decide(i: number, action: 'apply' | 'reject') {
    const p = lines[i]?.proposal
    if (!p) return
    try {
      let toBot = false
      if (action === 'apply') {
        const r = await applyProposal(p.id)
        toBot = !!r?.appliedRef?.startsWith('bot:')
        // Áp vào nháp của chính nhân viên AI này → màn Dạy AI nạp lại để thấy thiết lập mới (không bị tự lưu đè).
        if (toBot) window.dispatchEvent(new CustomEvent(TRAIN_BOT_RELOAD_EVENT, { detail: { botId } }))
      } else await rejectProposal(p.id)
      setLines((l) => l.map((x, j) => (j === i ? { ...x, proposalState: action === 'apply' ? 'applied' : 'rejected' } : x)))
      toast.success(action === 'reject' ? t('testPanel.proposalRejected')
        : toBot ? t('testPanel.proposalAppliedToBot')
        : t('testPanel.proposalApplied'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  function newSession() {
    sessionRef.current = null
    setLines([])
  }

  return (
    <>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto rounded-lg border bg-muted/30 p-3">
        {lines.length === 0 && (
          <div className="space-y-2 py-2">
            <p className="text-center text-xs text-muted-foreground">
              <Trans t={t} i18nKey="testPanel.guideIntro" components={{ b: <b /> }} />
            </p>
            {GUIDE_QUICK.map((q) => (
              <button key={q} type="button" disabled={busy} onClick={() => void send(q)}
                className="w-full rounded-md border bg-background px-3 py-2 text-left text-sm hover:border-primary/60">
                {q}
              </button>
            ))}
          </div>
        )}
        {lines.map((l, i) => (
          <div key={i} className={cn('flex', l.role === 'user' ? 'justify-end' : 'justify-start')}>
            <div className={cn(
              'max-w-[90%] space-y-2 whitespace-pre-wrap rounded-lg px-3 py-2 text-sm',
              l.role === 'user' ? 'bg-primary text-primary-foreground' : 'border bg-background',
            )}>
              <p>{l.text}</p>
              {l.proposal && (
                <div className="space-y-1.5 rounded border bg-muted/40 p-2 text-xs">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline">
                      {l.proposal.targetType === 'logic_doc' && (l.proposal.targetSubtype === 'persona' || l.proposal.targetSubtype === 'playbook')
                        ? t('testPanel.thisAi')
                        : PROPOSAL_TARGET_LABELS[l.proposal.targetType] ?? l.proposal.targetType}
                    </Badge>
                    {l.proposal.targetSubtype && <Badge variant="secondary">{PROPOSAL_SUBTYPE_LABELS[l.proposal.targetSubtype] ?? l.proposal.targetSubtype}</Badge>}
                  </div>
                  {l.proposal.rationale && <p className="text-muted-foreground">{l.proposal.rationale}</p>}
                  <details>
                    <summary className="cursor-pointer select-none font-medium">{t('testPanel.viewProposal')}</summary>
                    <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words font-sans">{l.proposal.proposedValue}</pre>
                  </details>
                  {l.proposalState ? (
                    <p className={l.proposalState === 'applied' ? 'text-success' : 'text-muted-foreground'}>
                      {l.proposalState === 'applied' ? t('testPanel.appliedMark') : t('testPanel.skipped')}
                    </p>
                  ) : (
                    <div className="flex gap-1.5">
                      <Button size="sm" className="h-7" onClick={() => void decide(i, 'apply')}>{t('common:actions.apply')}</Button>
                      <Button size="sm" variant="ghost" className="h-7" onClick={() => void decide(i, 'reject')}>{t('testPanel.skip')}</Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && <p className="flex items-center gap-1.5 text-xs italic text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('testPanel.thinking')}</p>}
        <div ref={endRef} />
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">
        {t('testPanel.guideNote')}
        {lines.length > 0 && <button type="button" className="ml-1 text-primary hover:underline" onClick={newSession}>{t('testPanel.newConversation')}</button>}
      </p>
      <div className="mt-1 flex gap-2">
        <Input
          placeholder={t('testPanel.guidePlaceholder')}
          value={input} disabled={busy}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send() } }}
        />
        <Button onClick={() => void send()} disabled={!input.trim() || busy}><SendHorizonal className="h-4 w-4" /></Button>
      </div>
    </>
  )
}
